import React, { useState, useCallback, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Chip,
} from "@mui/material";
import { MdOutlineCopyAll } from "react-icons/md";
import { showNotification } from "./NotificationProvider";
import Popup from "devextreme-react/popup";
import TagBox from "devextreme-react/tag-box";
import SelectBox from "devextreme-react/select-box";

import { useDispatch, useSelector } from "react-redux";
import {
  updateUser,
  changeUserPassword,
  blockUser,
  assignUserToTrader,
  updateAdminType,
  updateTraderKassaPermission,
  getMe,
  getAllTeams,
  assignUserToTeam,
} from "../store";

const UserManagementPanel = ({ users, traderList, fetchUsers }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.user?.user);
  const teams = useSelector((state) => state.team.teams);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [dropPermission, setDropPermission] = useState(false);
  const [traders, setTraders] = useState([]); // Add this state
  const [newPassword, setNewPassword] = useState("");
  const [editPopupVisible, setEditPopupVisible] = useState(false);
  const [passwordPopupVisible, setPasswordPopupVisible] = useState(false);

  useEffect(() => {
    if (!editPopupVisible && selectedUser !== null) {
      setSelectedUser(null);
    }
  }, [editPopupVisible, selectedUser]);

  // Fetch teams if current user is superadmin
  useEffect(() => {
    if (
      currentUser &&
      currentUser.role === "admin" &&
      currentUser.adminType === "superadmin"
    ) {
      dispatch(getAllTeams());
    }
  }, [currentUser, dispatch]);

  const handleUserSelect = (user) => {
    console.log("=== USER SEÇİLDİ ===");
    console.log("Original user:", user);
    const selectedUserData = {
      ...user,
      userId: user._id,
      traders: user.traders.map((trader) => trader._id),
      permissions: user.permissions,
      adminType: user.adminType || "full", // Default to "full" if not set
      traderKassaManagement: user.traderKassaManagement || {
        enabled: false,
        authorizedTraders: [],
      },
      team: user.team?._id || null, // Team ID or null
      teamRole: user.teamRole || "Team Person", // Default to "Team Person"
    };
    console.log("Selected user data:", selectedUserData);
    setSelectedUser(selectedUserData);
    setEditPopupVisible(true);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    console.log(`Input changed - Field: ${name}, Value:`, value);
    setSelectedUser((prevUser) => {
      const updatedUser = {
        ...prevUser,
        [name]: value,
      };
      console.log("Updated selectedUser:", updatedUser);
      return updatedUser;
    });
  };

  const handlePermissionChange = (e) => {
    const value = e.value;
    setSelectedUser((prevUser) => ({
      ...prevUser,
      permissions: value,
    }));
  };

  const handleDropDepositPermissionChange = () => {
    setSelectedUser((prevUser) => ({
      ...prevUser,
      dropDepositPermission: !prevUser.dropDepositPermission,
    }));
  };

  const handleTradersChange = (e) => {
    if (!selectedUser) return; // Prevent the function from running if selectedUser is null

    if (e.value.includes("all")) {
      setSelectedUser((prevUser) => ({
        ...prevUser,
        traders: traderList.map((trader) => trader._id),
      }));
      return;
    }

    const selectedTraderIds = e.value.map((id) => id);
    if (
      JSON.stringify(selectedTraderIds) !==
      JSON.stringify(selectedUser?.traders)
    ) {
      setSelectedUser((prevUser) => ({
        ...prevUser,
        traders: selectedTraderIds,
      }));
    }
  };

  const handleKassaTradersChange = (e) => {
    if (!selectedUser) return;

    const selectedTraderNames = e.value;
    setSelectedUser((prevUser) => ({
      ...prevUser,
      traderKassaManagement: {
        ...prevUser.traderKassaManagement,
        authorizedTraders: selectedTraderNames,
      },
    }));
  };

  const handleKassaEnabledChange = () => {
    setSelectedUser((prevUser) => ({
      ...prevUser,
      traderKassaManagement: {
        ...prevUser.traderKassaManagement,
        enabled: !prevUser.traderKassaManagement?.enabled,
      },
    }));
  };

  const handleSave = async () => {
    try {
      console.log("=== SAVE BAŞLADI ===");
      console.log("Selected User:", selectedUser);

      // Prepare update data with only necessary fields
      const updateData = {
        userId: selectedUser.userId,
        name: selectedUser.name,
        lastName: selectedUser.lastName,
        email: selectedUser.email,
        role: selectedUser.role,
        permissions: selectedUser.permissions,
        traders: selectedUser.traders,
        dropDepositPermission: selectedUser.dropDepositPermission,
      };

      // Add assignedTraderName if role is trader
      if (selectedUser.role === "trader") {
        updateData.assignedTraderName = selectedUser.assignedTraderName || "";
      }

      // Add adminType if role is admin
      if (selectedUser.role === "admin") {
        updateData.adminType = selectedUser.adminType || "full";
      }

      console.log("Update Data to send:", updateData);

      // Update user basic info
      const updateResult = await dispatch(updateUser(updateData)).unwrap();
      console.log("Update User Result:", updateResult);

      // If user role is admin and has adminType, update admin type
      if (selectedUser.role === "admin" && selectedUser.adminType) {
        try {
          console.log("Updating admin type to:", selectedUser.adminType);
          const adminTypeResult = await dispatch(
            updateAdminType({
              userId: selectedUser.userId,
              adminType: selectedUser.adminType,
            })
          ).unwrap();
          console.log("Admin Type Update Result:", adminTypeResult);
        } catch (adminTypeError) {
          console.error("Failed to update admin type:", adminTypeError);
          showNotification(
            "Warning",
            "warning",
            "User updated but admin type update failed."
          );
        }
      }

      // If user role is admin, update trader kassa management permission
      if (selectedUser.role === "admin" && selectedUser.traderKassaManagement) {
        try {
          console.log(
            "Updating trader kassa management:",
            selectedUser.traderKassaManagement
          );
          const kassaResult = await dispatch(
            updateTraderKassaPermission({
              userId: selectedUser.userId,
              enabled: selectedUser.traderKassaManagement.enabled,
              authorizedTraders:
                selectedUser.traderKassaManagement.authorizedTraders,
            })
          ).unwrap();
          console.log("✅ Trader Kassa Management Update Result:", kassaResult);
          console.log("✅ Updated user from response:", kassaResult.user);

          // Show success notification
          showNotification(
            "Success",
            "success",
            "Trader Kassa Management updated successfully"
          );
        } catch (kassaError) {
          console.error(
            "❌ Failed to update trader kassa management:",
            kassaError
          );
          showNotification(
            "Error",
            "error",
            `Failed to update trader kassa management: ${kassaError}`
          );
        }
      }

      // If user role is trader and has assignedTraderName, assign to trader
      if (selectedUser.role === "trader" && selectedUser.assignedTraderName) {
        try {
          console.log("Assigning trader:", selectedUser.assignedTraderName);
          const traderResult = await dispatch(
            assignUserToTrader({
              userId: selectedUser.userId,
              traderName: selectedUser.assignedTraderName,
            })
          ).unwrap();
          console.log("Trader Assignment Result:", traderResult);
        } catch (traderError) {
          console.error("Failed to assign user to trader:", traderError);
          showNotification(
            "Warning",
            "warning",
            "User updated but trader assignment failed."
          );
        }
      }

      // If current user is superadmin, handle team assignment
      if (
        currentUser &&
        currentUser.role === "admin" &&
        currentUser.adminType === "superadmin"
      ) {
        try {
          console.log("Assigning team:", selectedUser.team);
          await dispatch(
            assignUserToTeam({
              userId: selectedUser.userId,
              teamId: selectedUser.team || null,
              teamRole: selectedUser.team ? selectedUser.teamRole : undefined,
            })
          ).unwrap();
          console.log("Team Assignment Result: Success");
        } catch (teamError) {
          console.error("Failed to assign user to team:", teamError);
          showNotification(
            "Warning",
            "warning",
            "User updated but team assignment failed."
          );
        }
      }

      showNotification("Success", "success", "User updated successfully.");
      console.log("Fetching updated users...");
      await fetchUsers();

      // If user updated themselves, refresh current user state
      if (currentUser && currentUser._id === selectedUser.userId) {
        console.log(
          "User updated themselves, refreshing current user state..."
        );
        await dispatch(getMe()).unwrap();
        showNotification(
          "Info",
          "info",
          "Your permissions have been updated. Changes will take effect now."
        );
      }

      setEditPopupVisible(false);
      console.log("=== SAVE TAMAMLANDI ===");
    } catch (error) {
      console.error("Failed to update user:", error);
      console.error("Error details:", error.response?.data || error.message);
      showNotification("Error", "danger", "Failed to update user.");
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      showNotification(
        "Success",
        "success",
        "Google Authenticator Code copied to clipboard!"
      );
    });
  };

  const handleBlockUnblock = async (user) => {
    try {
      const updatedStatus = !user.blocked;

      await dispatch(
        blockUser({ userId: user._id, block: updatedStatus })
      ).unwrap();

      showNotification(
        "Success",
        "success",
        `User ${updatedStatus ? "blocked" : "unblocked"} successfully.`
      );

      await fetchUsers();
    } catch (error) {
      console.error("Failed to update user:", error);
      showNotification("Error", "error", "Failed to update user.");
    }
  };

  const handlePasswordChangeSave = () => {
    try {
      dispatch(
        changeUserPassword({
          userId: selectedUserForPassword._id,
          newPassword: newPassword,
        })
      );

      showNotification("Success", "success", "Password updated successfully.");

      fetchUsers();
      setNewPassword("");
      setPasswordPopupVisible(false);
    } catch (error) {
      console.error("Failed to update password:", error);
      showNotification("Error", "danger", "Failed to update password.");
    }
  };

  const handlePasswordChangeClick = (user) => {
    setSelectedUserForPassword(user);
    setPasswordPopupVisible(true);
  };

  const handleHidingEditPopup = () => {
    // setSelectedUser(null);
    // setPermissions([]);
    // setDropPermission(false);
    // setTraders([]);
    setEditPopupVisible(false);
  };

  const renderEditPopup = useCallback(
    () => (
      <div
        className="popup-edit-user"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <TextField
          label="Name"
          name="name"
          value={selectedUser?.name || ""}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          sx={{
            "& .MuiInputBase-input": { fontSize: "1.5rem", color: "#E0E6ED" },
            "& .MuiInputLabel-root": { fontSize: "1.5rem", color: "#E0E6ED" },
          }}
        />
        <TextField
          label="Email"
          name="email"
          value={selectedUser?.email || ""}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          sx={{
            "& .MuiInputBase-input": { fontSize: "1.5rem", color: "#E0E6ED" },
            "& .MuiInputLabel-root": { fontSize: "1.5rem", color: "#E0E6ED" },
          }}
        />
        <TextField
          label="Last Name"
          name="lastName"
          value={selectedUser?.lastName || ""}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          sx={{
            "& .MuiInputBase-input": { fontSize: "1.5rem", color: "#E0E6ED" },
            "& .MuiInputLabel-root": { fontSize: "1.5rem", color: "#E0E6ED" },
          }}
        />
        <label>Traders</label>
        <TagBox
          items={[{ _id: "all", traderName: "All Traders" }, ...traderList]}
          displayExpr="traderName"
          valueExpr="_id"
          value={selectedUser?.traders || []} // Ensure value is an array of IDs
          onValueChanged={handleTradersChange} // Updated handler
          showSelectionControls={true}
          placeholder="Select Traders"
          width="100%"
          style={{ marginTop: "2px", marginBottom: "1rem" }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedUser?.dropDepositPermission || false}
              onChange={handleDropDepositPermissionChange}
              sx={{ color: "#E0E6ED" }}
            />
          }
          label="Drop Deposit Permission"
          sx={{
            "& .MuiTypography-root": {
              fontSize: "1.5rem",
              color: "#E0E6ED",
            },
          }}
        />

        {/* Role Selection */}
        <label
          style={{
            color: "#E0E6ED",
            fontSize: "1.5rem",
            marginTop: "1rem",
            marginBottom: "0.5rem",
          }}
        >
          Role
        </label>
        <SelectBox
          items={["person", "admin", "trader"]}
          value={selectedUser?.role || "person"}
          onValueChanged={(e) => {
            handleInputChange({ target: { name: "role", value: e.value } });
          }}
          placeholder="Select Role"
          width="100%"
          style={{ marginBottom: "1rem" }}
        />

        {/* Admin Type Selection - Only show if role is admin */}
        {selectedUser?.role === "admin" && (
          <>
            <label
              style={{
                color: "#E0E6ED",
                fontSize: "1.5rem",
                marginBottom: "0.5rem",
              }}
            >
              Admin Type
            </label>
            <SelectBox
              items={["full", "c2c", "superadmin"]}
              value={selectedUser?.adminType || "full"}
              onValueChanged={(e) => {
                handleInputChange({
                  target: { name: "adminType", value: e.value },
                });
              }}
              placeholder="Select Admin Type"
              width="100%"
              style={{ marginBottom: "1rem" }}
            />
          </>
        )}

        {/* Trader Assignment - Only show if role is trader */}
        {selectedUser?.role === "trader" && (
          <>
            <label
              style={{
                color: "#E0E6ED",
                fontSize: "1.5rem",
                marginBottom: "0.5rem",
              }}
            >
              Assign to Trader
            </label>
            <SelectBox
              items={traderList}
              displayExpr="traderName"
              valueExpr="traderName"
              value={selectedUser?.assignedTraderName || ""}
              onValueChanged={(e) => {
                handleInputChange({
                  target: { name: "assignedTraderName", value: e.value },
                });
              }}
              placeholder="Select Trader"
              searchEnabled={true}
              width="100%"
              style={{ marginBottom: "1rem" }}
            />
          </>
        )}

        {/* Team Assignment - Only show if current user is superadmin */}
        {currentUser &&
          currentUser.role === "admin" &&
          currentUser.adminType === "superadmin" && (
            <>
              <label
                style={{
                  color: "#E0E6ED",
                  fontSize: "1.5rem",
                  marginTop: "1rem",
                  marginBottom: "0.5rem",
                }}
              >
                Team (Optional)
              </label>
              <SelectBox
                items={teams}
                displayExpr="teamName"
                valueExpr="_id"
                value={selectedUser?.team || null}
                onValueChanged={(e) => {
                  handleInputChange({
                    target: { name: "team", value: e.value },
                  });
                }}
                placeholder="Select Team"
                searchEnabled={true}
                width="100%"
                style={{ marginBottom: "1rem" }}
                showClearButton={true}
              />

              {/* Team Role - Only show if team is selected */}
              {selectedUser?.team && (
                <>
                  <label
                    style={{
                      color: "#E0E6ED",
                      fontSize: "1.3rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Team Role
                  </label>
                  <SelectBox
                    items={["Team Admin", "Team Person"]}
                    value={selectedUser?.teamRole || "Team Person"}
                    onValueChanged={(e) => {
                      handleInputChange({
                        target: { name: "teamRole", value: e.value },
                      });
                    }}
                    placeholder="Select Team Role"
                    width="100%"
                    style={{ marginBottom: "1rem" }}
                  />
                </>
              )}
            </>
          )}

        {/* Trader Kassa Management - Only show if role is admin */}
        {selectedUser?.role === "admin" && (
          <>
            <label
              style={{
                color: "#E0E6ED",
                fontSize: "1.5rem",
                marginTop: "1rem",
                marginBottom: "0.5rem",
              }}
            >
              Trader Kassa Management
            </label>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    selectedUser?.traderKassaManagement?.enabled || false
                  }
                  onChange={handleKassaEnabledChange}
                  sx={{ color: "#E0E6ED" }}
                />
              }
              label="Enabled"
              sx={{
                "& .MuiTypography-root": {
                  fontSize: "1.3rem",
                  color: "#E0E6ED",
                },
                marginBottom: "0.5rem",
              }}
            />
            {selectedUser?.traderKassaManagement?.enabled && (
              <>
                <label
                  style={{
                    color: "#E0E6ED",
                    fontSize: "1.3rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  Authorized Traders
                </label>
                <TagBox
                  items={traderList}
                  displayExpr="traderName"
                  valueExpr="traderName"
                  value={
                    selectedUser?.traderKassaManagement?.authorizedTraders || []
                  }
                  onValueChanged={handleKassaTradersChange}
                  showSelectionControls={true}
                  placeholder="Select Authorized Traders"
                  width="100%"
                  style={{ marginBottom: "1rem" }}
                />
              </>
            )}
          </>
        )}

        <TagBox
          items={["Deposit", "Withdrawal"]}
          value={selectedUser?.permissions || []}
          onValueChanged={handlePermissionChange}
          showSelectionControls={true}
          placeholder="Select Permissions"
          width="100%"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          sx={{
            marginTop: "1.6rem",
            fontSize: "1.5rem",
            backgroundColor: "#556CD6",
          }}
        >
          Save
        </Button>
      </div>
    ),
    [selectedUser, traderList]
  );

  const renderPasswordPopup = useCallback(
    () => (
      <div className="popup-change-password">
        <h3>
          Change Password for {selectedUserForPassword?.name || "Undefined"}
        </h3>
        <TextField
          label="New Password"
          name="newPassword"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          fullWidth
          margin="normal"
          sx={{
            "& .MuiInputBase-input": { fontSize: "1.5rem", color: "#E0E6ED" },
            "& .MuiInputLabel-root": { fontSize: "1.5rem", color: "#E0E6ED" },
          }}
        />
        <Button
          variant="contained"
          color="secondary"
          onClick={handlePasswordChangeSave}
          sx={{
            marginTop: "1.6rem",
            fontSize: "1.5rem",
            backgroundColor: "#FF4081",
          }}
        >
          Save Password
        </Button>
      </div>
    ),
    [newPassword, selectedUserForPassword]
  );

  return (
    <div
      className="control-panel-user-list"
      style={{
        backgroundColor: "#1C273A",
        minHeight: "100vh",
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          margin: "1rem auto",
          backgroundColor: "#2E3B55", // Darker background for the table
          color: "#E0E6ED", // Light font color
        }}
      >
        <Table
          sx={{
            "& .MuiTableCell-root": {
              fontSize: "1.2rem", // Increase the font size
              color: "#E0E6ED", // Light font color for table cells
              borderBottom: "1px solid #3A4663", // Adjust the border color to match the theme
            },
            "& .MuiTableHead-root": {
              backgroundColor: "#3A4663", // Slightly darker background for the header
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Admin Type</TableCell>
              <TableCell>Assigned Trader</TableCell>
              <TableCell>Team</TableCell>
              <TableCell>Team Role</TableCell>
              <TableCell>Kassa Management</TableCell>
              <TableCell>Google Authenticator Code</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>Traders</TableCell>
              <TableCell>Drop Permission</TableCell>
              <TableCell>Actions</TableCell>
              <TableCell>Change Password</TableCell>
              <TableCell>Block User</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user._id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <span
                    style={{
                      color:
                        user.role === "admin"
                          ? "#4CAF50"
                          : user.role === "trader"
                          ? "#FF9800"
                          : "#E0E6ED",
                      fontWeight:
                        user.role === "admin" || user.role === "trader"
                          ? "bold"
                          : "normal",
                    }}
                  >
                    {user.role
                      ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
                      : "Person"}
                  </span>
                </TableCell>
                <TableCell>
                  {user.role === "admin" ? (
                    <span
                      style={{
                        color:
                          user.adminType === "full" ? "#2196F3" : "#9C27B0",
                        fontWeight: "bold",
                      }}
                    >
                      {user.adminType
                        ? user.adminType.charAt(0).toUpperCase() +
                          user.adminType.slice(1)
                        : "Full"}
                    </span>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  {user.role === "trader"
                    ? user.assignedTraderName || "-"
                    : "-"}
                </TableCell>
                <TableCell>
                  {user.team ? (
                    <span style={{ color: "#2196F3", fontWeight: "bold" }}>
                      {user.team.teamName}
                    </span>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  {user.team && user.teamRole ? (
                    <span
                      style={{
                        color:
                          user.teamRole === "Team Admin"
                            ? "#FF9800"
                            : "#9E9E9E",
                        fontWeight:
                          user.teamRole === "Team Admin" ? "bold" : "normal",
                      }}
                    >
                      {user.teamRole}
                    </span>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  {user.role === "admin" &&
                  user.traderKassaManagement?.enabled ? (
                    <span style={{ color: "#4CAF50", fontWeight: "bold" }}>
                      ✓ Enabled (
                      {user.traderKassaManagement.authorizedTraders?.length ||
                        0}{" "}
                      traders)
                    </span>
                  ) : user.role === "admin" ? (
                    <span style={{ color: "#757575" }}>Disabled</span>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Click to copy">
                    <Button
                      onClick={() =>
                        handleCopyCode(user.googleAuthenticatorCode)
                      }
                      startIcon={<MdOutlineCopyAll />}
                      sx={{
                        color: "#E0E6ED", // Light font color for the button
                        textTransform: "none",
                      }}
                    >
                      {user.googleAuthenticatorCode.slice(0, 10)}...
                    </Button>
                  </Tooltip>
                </TableCell>
                <TableCell>{user.permissions.join(", ")}</TableCell>
                <TableCell>
                  <Chip
                    label={`${user.traders.length} trader${
                      user.traders.length !== 1 ? "s" : ""
                    }`}
                    sx={{
                      backgroundColor: "#556CD6",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Checkbox checked={user.dropDepositPermission} disabled />
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleUserSelect(user)}
                  >
                    Edit
                  </Button>
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handlePasswordChangeClick(user)}
                  >
                    Change Password
                  </Button>
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    // color={user.blocked ? "error" : "default"}
                    onClick={() => handleBlockUnblock(user)}
                    sx={{
                      width: "7.7rem",
                      backgroundColor: user.blocked ? "#FF5733" : "#28a745",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: user.blocked ? "#FF2E00" : "#218838",
                      },
                    }}
                  >
                    {user.blocked ? "Unblock" : "Block"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Popup
        width={600}
        height={
          selectedUser?.role === "admin" &&
          selectedUser?.traderKassaManagement?.enabled
            ? 720
            : 520
        }
        showTitle={true}
        dragEnabled={false}
        hideOnOutsideClick={true}
        visible={editPopupVisible}
        onHiding={handleHidingEditPopup}
        showCloseButton={true}
        container=".dx-viewport"
        title={`User Editing for, ${selectedUser?.name || ""}`}
        animation={{
          show: { type: "slide", duration: 400, from: { top: -50 } },
        }}
        position={{ my: "center", at: "center", of: window }}
        contentRender={renderEditPopup}
      />

      <Popup
        width={600}
        height={270}
        showTitle={true}
        dragEnabled={false}
        hideOnOutsideClick={true}
        visible={passwordPopupVisible}
        onHiding={() => {
          setPasswordPopupVisible(false);
          setNewPassword("");
        }}
        contentRender={renderPasswordPopup}
        showCloseButton={true}
        container=".dx-viewport"
        title="Change Password"
        animation={{
          show: { type: "slide", duration: 400, from: { top: -50 } },
        }}
        position={{ my: "center", at: "center", of: window }}
      />
    </div>
  );
};

export default UserManagementPanel;
